import { PercentPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { ChartService } from '@services/chart.service';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartLabelFormatterParams, AgChartOptions } from 'ag-charts-community';
import { FormatNumberWithSuffixPipe } from 'ngx-lib';
import { CHART_COLORS } from '../../themes/chart-themes';

export interface IBidVsAwardedChartConfig {
    title: string;
    data: IBidVsAwardedChartData[];
    descriptionHelpText?: string;
}

export interface IBidVsAwardedChartData {
    period: string;
    bidTotal: number;
    awardedTotal: number;
    awardedBid: number;
    awardedRate: number;
}

@Component({
    selector: 'app-bid-vs-awarded-chart',
    templateUrl: './bid-vs-awarded-chart.component.html',
    styleUrls: ['./bid-vs-awarded-chart.component.scss'],
    imports: [AgChartsModule],
    providers: [FormatNumberWithSuffixPipe, PercentPipe]
})
export class BidVsAwardedChartComponent {
    readonly config = input<IBidVsAwardedChartConfig>();

    private readonly chartService = inject(ChartService);
    private readonly formatNumberWithSuffixPipe = inject(FormatNumberWithSuffixPipe);
    private readonly percentPipe = inject(PercentPipe);

    commonConfig = {
        label: {
            formatter: ({ value }: AgChartLabelFormatterParams<number>) => {
                return value > 0 ? this.formatNumberWithSuffixPipe.transform(value) : '';
            }
        }
    };

    readonly chartOptions = computed<AgChartOptions | undefined>(() => {
        const config = this.config();
        if (!config) return undefined;

        return {
            theme: this.chartService.getTheme(),
            height: 250,
            legend: {
                toggleSeries: false
            },
            title: {
                text: config.title
            },
            data: config.data,
            series: [
                {
                    type: 'bar',
                    xKey: 'period',
                    yKey: 'bidTotal',
                    yName: 'Bid Total',
                    fillOpacity: 0.9,
                    ...this.commonConfig
                },
                {
                    type: 'bar',
                    xKey: 'period',
                    yKey: 'awardedBid',
                    yName: 'Awarded Bid',
                    fillOpacity: 0.9,
                    ...this.commonConfig
                },
                {
                    type: 'bar',
                    xKey: 'period',
                    yKey: 'awardedTotal',
                    yName: 'Awarded Total',
                    fillOpacity: 0.9,
                    ...this.commonConfig
                },
                {
                    type: 'line',
                    xKey: 'period',
                    yKey: 'awardedRate',
                    yName: 'Awarded Rate',

                    lineDash: [8, 8],
                    stroke: CHART_COLORS.rates,
                    marker: {
                        shape: 'circle',
                        fill: CHART_COLORS.rates,
                        size: 10
                    },
                    tooltip: {
                        renderer: ({ datum }) => {
                            return {
                                backgroundColor: CHART_COLORS.rates,
                                content: `${datum.period}: ${this.percentPipe.transform(datum.awardedRate, '1.0-0')}`
                            };
                        }
                    }
                }
            ],
            axes: [
                {
                    type: 'category',
                    position: 'bottom'
                },
                {
                    type: 'number',
                    position: 'left',
                    title: {
                        text: 'Quantity'
                    },
                    label: {
                        formatter: ({ value }) => {
                            return this.formatNumberWithSuffixPipe.transform(value);
                        }
                    },
                    keys: ['bidTotal', 'awardedBid', 'awardedTotal'],

                    nice: true
                },
                {
                    position: 'right',
                    type: 'number',
                    title: {
                        text: 'Awarded Rate (%)'
                    },
                    keys: ['awardedRate'],
                    nice: true,
                    min: 0,
                    max: 1,
                    interval: {
                        values: [0, 0.5, 1]
                    },
                    label: {
                        formatter: ({ value }) => `${this.percentPipe.transform(value, '1.0-0')}`
                    }
                }
            ]
        };
    });
}
